import type { TFunction } from 'i18next';

import type { CalendarEvent } from './model';

/**
 * What to tell the reader about an item. Explicit completion comes first and is never replaced
 * by a time-based state; "past" is only the derived fact that an EVENT has ended (it is not
 * completed and is not overdue). Plain tasks have only completed / overdue.
 */
export type ItemStatus = 'completed' | 'overdue' | 'current' | 'past' | 'upcoming';

export function statusOf(event: CalendarEvent): ItemStatus | null {
  if (event.card.completed_at !== null) return 'completed';
  if (event.card.is_overdue) return 'overdue';
  return event.phase ?? null;
}

/** An archived task: not on the board, kept with its dates (a separate flag, never a status). */
export const isArchived = (event: CalendarEvent): boolean =>
  event.card.archived_at !== null && event.card.archived_at !== undefined;

export function statusLabel(status: ItemStatus, t: TFunction): string {
  return status === 'completed' || status === 'overdue'
    ? t(`calendar.${status}`)
    : t(`calendar.phase.${status}`);
}

/** The status worth printing in a compact meta line (an upcoming event needs no label). */
export function metaStatus(event: CalendarEvent, t: TFunction): string {
  const status = statusOf(event);
  const label = status && status !== 'upcoming' ? statusLabel(status, t) : '';
  return [label, isArchived(event) ? t('calendar.archivedTag') : ''].filter(Boolean).join(' · ');
}
